// S4 · Import detail (`/onboarding/imports/$id`, Module 02 §4 S4, §3.3).
//
// One screen for the whole §3.3 state machine:
//
//   uploaded ─▶ parsing ─▶ mapping ─▶ (re-check loop) ─▶ validated ─▶ committing ─▶ committed
//                  │           │                            │
//                  └───────────┴────── failed ◀──────────────┘
//        anything before committing ─▶ discarded
//
// Each status renders the ONE thing that can happen next, so a user is never
// offered a button the server will refuse with IMPORT_STATE_INVALID. The two
// async worker stages (parsing, committing) poll; everything else is a direct
// response to what the user just did.
//
// Not a wizard STEP screen: it is reached from step 2 and step 4, and stays
// usable after go-live (imports are not wizard mutations, so they never answer
// 409 ALREADY_LIVE) — hence no StepContainer and no frozen gate.
//
// §7 shows up twice: the lane permission (`onboarding.import.<lane>`) mirrors
// the server's gate on every action, and money columns arrive redacted with
// `redacted_fields[]` naming what was withheld — rendered as locks by the grid.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { CardSkeleton, EmptyState } from "../../../components/states";
import { formatTimestamp } from "../../../lib/datetime";
import { UnauthorizedError } from "../../../lib/problem";
import {
  useCommitImport,
  useDiscardImport,
  useImport,
  useImportRows,
  useParseImport,
  useSetImportMapping,
  useValidateImport,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import {
  MAX_FILE_MB,
  MAX_ROWS,
  canCommit,
  canDiscard,
  canEditMapping,
  canParse,
  canValidate,
  commitImpact,
  commitOutcome,
  commitProgress,
  formatBytes,
  GROUP_KEY_FIELDS,
  hasCommitOutcome,
  hasRows,
  stepRouteForKind,
  type ImportJob,
  type ImportKind,
} from "./imports/import-model";
import { WizardDialog } from "../components/wizard-dialog";
import { ImportProblemNotice } from "./imports/import-problem-notice";
import { useImportLabels } from "./imports/labels";
import { MappingEditor } from "./imports/mapping-editor";
import { ImportRowGrid } from "./imports/row-grid";
import "./imports/imports.css";

/** Sample page the mapping editor reads the sheet's real header order from. */
const SAMPLE_ROWS = 20;

/**
 * The exits a page hosting this flow outside the wizard supplies (spec 09
 * §4.18's Import customers, P3-E2 PR E2-2c): its own way back, and the
 * committed job's action. Without them the job page is the wizard's S4, whose
 * back link and done action return to the lane's step.
 */
export interface ImportJobExits {
  /** Drawn where S4 draws its back link, on every stage and on a failed read. */
  readonly back: ReactNode;
  /** The committed stage's primary action. */
  readonly doneLabel: string;
  readonly onDone: () => void;
}

/** S4 · `/onboarding/imports/$id`: the wizard's home for one job. */
export function ImportDetailScreen() {
  const { id } = useParams({ from: "/onboarding/imports/$id" });
  return <ImportJobDetail id={id} />;
}

/**
 * One import job, start to finish (§3.3's state machine). Rendered by the
 * wizard's S4 and, with its own `exits`, by every page that hosts the flow in
 * another area, so there is one job page and no copy of it.
 */
export function ImportJobDetail({ id, exits }: { id: string; exits?: ImportJobExits }) {
  const { t, i18n } = useTranslation();
  const labels = useImportLabels();
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();

  const job = useImport(id, { pollWhileWorking: true });
  const parse = useParseImport(id);
  const validate = useValidateImport(id);
  const commit = useCommitImport(id);
  const discard = useDiscardImport(id);

  const status = job.data?.status;
  // Rows only exist once the file has been read (§3.3), so the sample the
  // mapping editor reads its header order from is not asked for before then —
  // the hook is disabled by an empty id rather than fetching a certain-empty page.
  const sample = useImportRows(status !== undefined && hasRows(status) ? id : "", {
    limit: SAMPLE_ROWS,
  });

  const remap = useSetImportMapping(id);

  const [allowPartial, setAllowPartial] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const autoParsed = useRef(false);

  /** "Check again" from the validated state: re-open the job by re-saving the
   * mapping it already has, then run the (synchronous) validation. */
  async function recheck() {
    if (job.data === undefined) return;
    try {
      await remap.mutateAsync({ column_mapping: job.data.column_mapping });
      validate.mutate();
    } catch {
      // Rendered by the problem notice in the validated panel.
    }
  }

  // 401 → sign-in, matching every other office screen.
  useEffect(() => {
    if (job.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [job.error, navigate]);

  // §3.3 step 1: the client uploads the bytes, then asks for the parse. S3 hands
  // off here right after the PUT, so the parse starts itself — once, guarded by
  // a ref, and only when this caller may actually run the lane.
  const mayRun = job.data !== undefined && perms.canImport(job.data.kind);
  useEffect(() => {
    if (status === undefined || !canParse(status) || autoParsed.current || !mayRun) return;
    autoParsed.current = true;
    // `parse` is a fresh mutation object every render, so it stays out of the
    // dependency list on purpose; the ref is the once-only guard.
    parse.mutate();
  }, [status, mayRun, parse]);

  if (job.isPending || job.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("onboarding.importDetail.loading")} />;
  }

  if (job.error !== null) {
    return (
      <section className="import-detail">
        {/* No job means no kind, so the fallback is the catalogue step. */}
        {exits?.back ?? <BackLink kind="products" />}
        <ImportProblemNotice error={job.error} onRetry={() => void job.refetch()} />
      </section>
    );
  }

  const data = job.data;
  const outcome = commitOutcome(data);
  const impact = commitImpact(data, allowPartial);
  const readOnly = !perms.canImport(data.kind);

  return (
    <section className="import-detail" data-testid="import-detail" data-status={data.status}>
      {exits?.back ?? <BackLink kind={data.kind} />}

      <header className="import-detail-head">
        <div>
          <h2>{data.file_name}</h2>
          <p className="import-detail-meta">
            <span>{labels.kindLabel(data.kind)}</span>
            <span>{formatBytes(data.file_size_bytes)}</span>
            <span>{formatTimestamp(data.created_at, i18n.language)}</span>
            <span>
              {t("onboarding.importShared.duplicatePolicy.label")}:{" "}
              {labels.policyLabel(data.duplicate_policy)}
            </span>
          </p>
          <p className="wizard-save-bar-note">
            {t("onboarding.importShared.duplicatePolicy.fixed")}
          </p>
        </div>
        <span className={`badge status-${data.status}`} data-testid="import-status">
          {labels.jobStatusLabel(data.status)}
        </span>
      </header>

      <Counters job={data} />

      {readOnly ? (
        <div
          className="state state-empty wizard-notice"
          role="status"
          data-testid="import-readonly"
        >
          <p className="state-title">{t("onboarding.importDetail.notPermittedTitle")}</p>
          <p className="state-body">{t("onboarding.importDetail.notPermittedBody")}</p>
        </div>
      ) : null}

      {/* ---- uploaded: the bytes are in S3, the parse has to be asked for ---- */}
      {data.status === "uploaded" ? (
        <div className="import-stage ds-card" data-testid="stage-uploaded">
          <h3>{t("onboarding.importDetail.parse.title")}</h3>
          <p>{t("onboarding.importDetail.parse.body")}</p>
          {parse.error !== null ? (
            <ImportProblemNotice
              kind={data.kind}
              error={parse.error}
              onRetry={() => {
                parse.reset();
              }}
            />
          ) : null}
          <Button
            variant="primary"
            disabled={readOnly}
            busy={parse.isPending}
            busyLabel={t("onboarding.importDetail.parse.starting")}
            onClick={() => {
              parse.mutate();
            }}
          >
            {t("onboarding.importDetail.parse.start")}
          </Button>
        </div>
      ) : null}

      {/* ---- parsing: async worker, the query polls ------------------------- */}
      {data.status === "parsing" ? (
        <div className="import-stage ds-card" role="status" data-testid="stage-parsing">
          <h3>{t("onboarding.importDetail.parsing.title")}</h3>
          <p>{t("onboarding.importDetail.parsing.body")}</p>
          <CardSkeleton lines={3} label={t("onboarding.importDetail.parsing.title")} />
        </div>
      ) : null}

      {/* ---- mapping / validated: the confirm-and-check loop ---------------- */}
      {canEditMapping(data.status) ? (
        <>
          {validate.error !== null ? (
            <ImportProblemNotice
              kind={data.kind}
              error={validate.error}
              onRetry={() => {
                validate.reset();
              }}
            />
          ) : null}
          <MappingEditor
            job={data}
            rows={sample.data?.items ?? []}
            readOnly={readOnly}
            validating={validate.isPending}
            canValidate={canValidate(data.status)}
            onValidate={() => {
              validate.mutate();
            }}
          />
        </>
      ) : null}

      {data.status === "validated" ? (
        <div className="import-stage ds-card" data-testid="stage-validated">
          <h3>{t("onboarding.importDetail.validated.title")}</h3>
          <p>
            {data.error_rows === 0
              ? t("onboarding.importDetail.validated.allGood")
              : t("onboarding.importDetail.validated.someBad", {
                  errors: data.error_rows,
                  total: data.total_rows,
                })}
          </p>
          <p className="wizard-save-bar-note">{t("onboarding.importDetail.validated.fixHint")}</p>

          {/* §4 S4 "say what Commit will do". Counts only — `created` vs
              `updated` is decided inside the owning module's committer by its
              natural-key lookup, so claiming a split here would be a guess. */}
          <div className="import-impact" data-testid="import-impact">
            <p className="state-title">{t("onboarding.importDetail.impact.title")}</p>
            <ul className="wizard-notice-list">
              <li>
                {t("onboarding.importDetail.impact.willLoad", {
                  count: impact.willLoad,
                  kind: labels.kindLabel(data.kind),
                })}
              </li>
              {impact.withWarnings > 0 ? (
                <li>
                  {t("onboarding.importDetail.impact.withWarnings", {
                    count: impact.withWarnings,
                  })}
                </li>
              ) : null}
              {data.error_rows > 0 ? (
                <li>
                  {allowPartial
                    ? t("onboarding.importDetail.impact.willSkip", { count: impact.willSkip })
                    : t("onboarding.importDetail.impact.blockedByErrors", {
                        count: data.error_rows,
                      })}
                </li>
              ) : null}
              <li>
                {t("onboarding.importDetail.impact.duplicates", {
                  policy: labels.policyLabel(impact.duplicatePolicy),
                })}
              </li>
            </ul>
            {/* There is no reverse endpoint yet (see the module PR follow-ups),
                so the user is told BEFORE the button, not after. */}
            <p className="state-detail import-impact-warning">
              {t("onboarding.importDetail.impact.irreversible")}
            </p>
          </div>

          {commit.error !== null ? (
            <ImportProblemNotice
              kind={data.kind}
              error={commit.error}
              onRetry={() => {
                commit.reset();
              }}
            />
          ) : null}
          {remap.error !== null ? (
            <ImportProblemNotice
              kind={data.kind}
              error={remap.error}
              onRetry={() => {
                remap.reset();
              }}
            />
          ) : null}

          {data.error_rows > 0 ? (
            <label className="import-toggle">
              <input
                type="checkbox"
                checked={allowPartial}
                disabled={readOnly}
                onChange={(e) => {
                  setAllowPartial(e.target.checked);
                }}
              />
              <span>{t("onboarding.importDetail.validated.allowPartial")}</span>
              <small className="filter-help">
                {GROUP_KEY_FIELDS[data.kind] !== undefined
                  ? t("onboarding.importDetail.validated.allowPartialHelpGrouped")
                  : t("onboarding.importDetail.validated.allowPartialHelp")}
              </small>
            </label>
          ) : null}

          <div className="import-actions">
            <Button
              variant="primary"
              disabled={
                readOnly || !canCommit(data.status) || (data.error_rows > 0 && !allowPartial)
              }
              busy={commit.isPending}
              busyLabel={t("onboarding.importDetail.validated.committing")}
              onClick={() => {
                commit.mutate({ allow_partial: allowPartial });
              }}
            >
              {commitLabel(data.kind, t)}
            </Button>
            {/* Re-checking a job the server has already checked means re-opening
                it: `PATCH /mapping` puts it back in `mapping` (§3.3), which is
                the only status `POST /validate` accepts. Doing both here keeps
                the fix-and-check loop one click, without faking a transition. */}
            <Button
              disabled={readOnly}
              busy={remap.isPending || validate.isPending}
              busyLabel={t("onboarding.importDetail.mapping.validating")}
              onClick={() => void recheck()}
            >
              {t("onboarding.importDetail.validated.revalidate")}
            </Button>
            {data.error_report_url !== null ? (
              <a className="wizard-header-link" href={data.error_report_url} download>
                {t("onboarding.importDetail.validated.errorReport")}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ---- committing: async worker, the query polls ---------------------- */}
      {data.status === "committing" ? (
        <div className="import-stage ds-card" role="status" data-testid="stage-committing">
          <h3>{t("onboarding.importDetail.committing.title")}</h3>
          <p>{t("onboarding.importDetail.committing.body")}</p>
          <progress max={1} value={commitProgress(data)} />
          <p className="wizard-save-bar-note">
            {t("onboarding.importDetail.committing.progress", {
              done: data.committed_rows,
              total: data.total_rows,
            })}
          </p>
        </div>
      ) : null}

      {/* ---- committed: the outcome, honestly split ------------------------- */}
      {data.status === "committed" ? (
        <div className="import-stage ds-card" data-testid="stage-committed">
          <h3>{t("onboarding.importDetail.committed.title")}</h3>
          <p>{t("onboarding.importDetail.committed.body", { committed: outcome.committed })}</p>
          {outcome.skipped > 0 ? (
            <p>{t("onboarding.importDetail.committed.skipped", { count: outcome.skipped })}</p>
          ) : null}
          {outcome.errors > 0 ? (
            <>
              <p>{t("onboarding.importDetail.committed.errors", { count: outcome.errors })}</p>
              <p className="wizard-save-bar-note">
                {t("onboarding.importDetail.committed.partialHint")}
              </p>
            </>
          ) : null}
          <div className="import-actions">
            {/* A navigation CTA is a Button whose onClick navigates — links are
                never styled as buttons (design-system gate §4.3b). */}
            <Button
              variant="primary"
              data-testid="import-committed-done"
              onClick={() => {
                if (exits !== undefined) {
                  exits.onDone();
                  return;
                }
                void navigate({ to: stepRouteForKind(data.kind) });
              }}
            >
              {exits?.doneLabel ?? t("onboarding.importDetail.committed.backToStep")}
            </Button>
            {/* The orders a committed orders job loaded, on the order list
                filtered to the job (spec 09 §4.19). */}
            {data.kind === "open_sales_orders" && outcome.committed > 0 ? (
              <Link
                to="/sales/orders"
                search={{ import_job: data.id }}
                className="wizard-header-link"
                data-testid="import-committed-orders"
              >
                {t("onboarding.importDetail.committed.seeOrders")}
              </Link>
            ) : null}
            {data.error_report_url !== null ? (
              <a className="wizard-header-link" href={data.error_report_url} download>
                {t("onboarding.importDetail.validated.errorReport")}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ---- failed: the parse/commit worker gave up ------------------------ */}
      {data.status === "failed" ? (
        <div className="import-stage ds-card state-error" role="alert" data-testid="stage-failed">
          <h3>{t("onboarding.importDetail.failed.title")}</h3>
          <p>{t("onboarding.importDetail.failed.body")}</p>
          <p className="state-detail">{t("onboarding.importDetail.failed.causesTitle")}</p>
          <ul className="wizard-notice-list">
            <li>{t("onboarding.importDetail.failed.causeEncoding")}</li>
            <li>
              {t("onboarding.importDetail.failed.causeTooLarge", {
                sizeMb: MAX_FILE_MB,
                maxRows: MAX_ROWS.toLocaleString(),
              })}
            </li>
            <li>{t("onboarding.importDetail.failed.causeMerged")}</li>
            <li>{t("onboarding.importDetail.failed.causeEmpty")}</li>
          </ul>
          <p>{t("onboarding.importDetail.failed.whatNext")}</p>
        </div>
      ) : null}

      {/* ---- discarded ------------------------------------------------------ */}
      {data.status === "discarded" ? (
        <EmptyState
          title={t("onboarding.importDetail.discarded.title")}
          body={t("onboarding.importDetail.discarded.body")}
        />
      ) : null}

      {hasRows(data.status) && data.status !== "discarded" ? <ImportRowGrid job={data} /> : null}

      {canDiscard(data.status) && !readOnly ? (
        <div className="import-actions import-danger">
          {discard.error !== null ? (
            <ImportProblemNotice
              kind={data.kind}
              error={discard.error}
              onRetry={() => {
                discard.reset();
              }}
            />
          ) : null}
          <Button
            busy={discard.isPending}
            busyLabel={t("onboarding.importDetail.validated.discarding")}
            onClick={() => {
              setConfirmingDiscard(true);
            }}
          >
            {t("onboarding.importDetail.validated.discard")}
          </Button>
        </div>
      ) : null}

      {/* A focus-managed dialog rather than `window.confirm`: it can name the
          file and its row count, it is styled, it works on a phone, and a
          Playwright walkthrough can actually drive it. */}
      {confirmingDiscard ? (
        <WizardDialog
          testId="import-discard-confirm"
          destructive
          title={t("onboarding.importDetail.discard.confirmTitle")}
          confirmLabel={t("onboarding.importDetail.validated.discard")}
          busy={discard.isPending}
          onConfirm={() => {
            setConfirmingDiscard(false);
            discard.mutate();
          }}
          onCancel={() => {
            setConfirmingDiscard(false);
          }}
        >
          <p>
            {t("onboarding.importDetail.discard.confirmBody", {
              file: data.file_name,
              rows: data.total_rows,
            })}
          </p>
        </WizardDialog>
      ) : null}
    </section>
  );
}

/** Where Commit says the rows go. */
function commitLabel(kind: ImportKind, t: ReturnType<typeof useTranslation>["t"]): string {
  if (kind === "customers") return t("onboarding.importDetail.validated.commitCustomers");
  if (kind === "open_sales_orders") return t("onboarding.importDetail.validated.commitOrders");
  return t("onboarding.importDetail.validated.commit");
}

/** Back to the step this lane belongs to — step 2 for catalogue kinds, step 4
 * for the opening-state kinds (§4 S3 / S7 both open this screen). */
function BackLink({ kind }: { kind: ImportKind }) {
  const { t } = useTranslation();
  return (
    <Link to={stepRouteForKind(kind)} className="wizard-header-link import-back">
      {t("onboarding.importDetail.back")}
    </Link>
  );
}

function Counters({ job }: { job: ImportJob }) {
  const { t } = useTranslation();
  // `committed_rows` is 0 until the commit worker has run, so `skipped` (total −
  // committed − errors) would report the WHOLE file as about to be dropped on a
  // freshly-validated job. Both cells only appear once the commit is real.
  const settled = hasCommitOutcome(job.status);
  const outcome = commitOutcome(job);
  const cells: readonly { key: string; value: number | null }[] = [
    { key: "total", value: job.total_rows },
    { key: "valid", value: job.valid_rows },
    { key: "warning", value: job.warning_rows },
    { key: "error", value: job.error_rows },
    { key: "committed", value: settled ? job.committed_rows : null },
    { key: "skipped", value: settled ? outcome.skipped : null },
  ];
  return (
    <dl className="import-counters" data-testid="import-counters">
      {cells.map((cell) => (
        <div key={cell.key} className={`import-counter counter-${cell.key}`}>
          <dt>{t(`onboarding.importShared.counters.${cell.key}`)}</dt>
          <dd>
            {cell.value ?? (
              <span
                className="import-counter-na"
                title={t("onboarding.importShared.counters.notYet")}
              >
                <span aria-hidden="true">—</span>
                <span className="visually-hidden">
                  {t("onboarding.importShared.counters.notYet")}
                </span>
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
